import { GetChargesListData, GetHSNCodeListData, InvoicingCategory } from 'src/components/pages/invoicing/types';
import ApiDto, { ApiDtoInvoicing } from 'src/dto/ApiDto';
import { GetAgeingViewReportData } from 'src/dto/response/getAgeingViewReports';
import { GetInvoicingBookingData } from 'src/dto/response/getInvoicingBookingDetails';
import { GetInvoicingExecutionData } from 'src/dto/response/getInvoicingExecutionDetails';
import { SearchInvoiceData } from 'src/dto/response/searchInvoice';
import { API_BASE_URL, INVOICE_API_BASE_URL } from 'src/globals';
import { APIResponseType } from 'src/Library/Types';
import { CInvoicingGetInvoiceDetailsResponse } from 'src/transformers/classes/CInvoicingGetInvoiceDetailsResponse';
import { CSearchShipmentForInvoiceResponse } from 'src/transformers/classes/CSearchShipmentForInvoiceResponse';

import { POST, GET, PUT, PATCH, DELETE } from './apiHandler';

import {
  INVOICE_RESOLVE,
  INVOICE_PAYMENT,
  INVOICE,
  GET_S3_UPLOAD_LINKS,
  INVOICE_FETCH_MASTER_DATA,
  INVOICE_SEARCH,
  INVOICE_ACTIVITY_FETCH,
  INVOICE_FETCH_COUNTS,
  INVOICE_DISPUTE,
  INVOICE_STATUS,
  SHIPPING_SEARCH,
  INVOICE_USERS,
  INVOICE_BALANCE,
  INVOICE_SHIPMENT,
  INVOICE_DOWNLOAD,
  INVOICE_REPORTS,
  INVOICE_SEARCH_SHIPMENT,
  EXECUTION,
  GET_CHARGES_LIST,
  INVOICE_ALL,
  INVOICE_APPROVE,
  INVOICE_SETTLE,
  INVOICE_DELETE,
  INVOICE_GET_DETAILS,
  INVOICE_CREATE,
  INVOICE_RAISE_DISPUTE,
  INVOICE_RESOLVE_DISPUTE,
  INVOICE_AGEING_VIEW_REPORTS,
  INVOICE_REPORTS_DOWNLOAD,
  INVOICE_SHIPMENT_BOOKING_META_REPORT,
  INVOICE_BOOKING,
  GET_INVOICE_ORG_SETTINGS,
  INQUIRY_HSN_CODE,
  INVOICE_IMPORT_SET,
  INVOICE_DOWNLOAD_VENDOR_PAYMENT,
  INVOICE_AGEING_VIEW_REPORTS_DOWNLOAD,
  INVOICE_HISTORICAL_EXCHANGE_RATE
} from './routes';

export const getS3Links = async (body) => {
  return await POST(`${API_BASE_URL}${GET_S3_UPLOAD_LINKS}`, body);
};

export const uploadToS3 = async (url: string, extension: string, file: any) => {
  return await PUT(url, extension, file);
};

export const raiseDispute = async (props: { invoiceId: number, body: typeof ApiDtoInvoicing.RaiseDisputeDto }) => {
  const { invoiceId, body } = props;
  return await POST(`${INVOICE_API_BASE_URL}${INVOICE_RAISE_DISPUTE(invoiceId)}`, body);
};

export const getDisputeHistory = async (invoiceId: number) => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE}/${invoiceId}${INVOICE_DISPUTE}`);
};

export const resolveDispute = async (invoiceId: number, body: typeof ApiDtoInvoicing.CreateInvoiceDto) => {
  return await PATCH(`${INVOICE_API_BASE_URL}${INVOICE_RESOLVE_DISPUTE(invoiceId)}`, body);
};

export const invoicePayment = async (invoiceId: number, body: typeof ApiDtoInvoicing.AddPaymentDto) => {
  return await POST(`${INVOICE_API_BASE_URL}${INVOICE_PAYMENT(invoiceId)}`, body);
};

export const approveInvoice = async (props: { invoiceId: number, [key: string]: any }) => {
  const { invoiceId, ...body } = props;
  return await PATCH(`${INVOICE_API_BASE_URL}${INVOICE_APPROVE(invoiceId)}`, body);
};

export const settleInvoice = async (invoiceId: number, body: any) => {
  return await PATCH(`${INVOICE_API_BASE_URL}${INVOICE_SETTLE(invoiceId)}`, body);
};

export const deleteInvoice = async (invoiceId: number, body: any) => {
  return await PATCH(`${INVOICE_API_BASE_URL}${INVOICE_DELETE(invoiceId)}`, body);
};

export const createInvoice = async (body: typeof ApiDtoInvoicing.CreateInvoiceDto) => {
  return await POST(`${INVOICE_API_BASE_URL}${INVOICE_CREATE}`, body);
};

export const fetchAllInvoice = async (body): Promise<APIResponseType<typeof ApiDtoInvoicing.FetchAllInvoiceDto>> => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_ALL}`, body);
};

export const fetchInvoiceMasterData = async () => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_FETCH_MASTER_DATA}`);
};

export const getInvoiceDetails = async (invoiceId: number): Promise<APIResponseType<CInvoicingGetInvoiceDetailsResponse>> => {
  const res = await GET(`${INVOICE_API_BASE_URL}${INVOICE_GET_DETAILS(invoiceId)}`);
  return {
    ...res,
    data: res.data ? new CInvoicingGetInvoiceDetailsResponse(res.data) : undefined,
  };
};

export const searchInvoice = async (params, signal?: AbortSignal): Promise<APIResponseType<SearchInvoiceData>> => {
  const res = await GET(`${INVOICE_API_BASE_URL}${INVOICE_SEARCH}`, params, undefined, undefined, signal);
  return res;
};

export const fetchActivityTimeline = async (params) => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_ACTIVITY_FETCH}`, params);
};

export const fetchInvoiceCount = async (params) => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_FETCH_COUNTS}`, params);
};

export const shipmentSearchApi = async (body) => {
  return await POST(`${API_BASE_URL}${EXECUTION}${SHIPPING_SEARCH}`, body);
};

export const getUserListApi = async (invoiceId: number) => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_USERS(invoiceId)}`);
};

export const getBalance = async (params) => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE}${INVOICE_BALANCE}`, params);
};

export const getShipmentDetails = async (params): Promise<APIResponseType<GetInvoicingExecutionData>> => {
  return await GET(`${API_BASE_URL}${INVOICE_SHIPMENT}/${params.shipmentId}${params?.invoiceDate ? `?invoiceDate=${params.invoiceDate}` : ''}`);
};

export const getBookingDetails = async (params): Promise<APIResponseType<GetInvoicingBookingData>> => {
  return await GET(`${API_BASE_URL}${INVOICE_BOOKING}/${params.shipmentId}${params?.invoiceDate ? `?invoiceDate=${params.invoiceDate}` : ''}`);
};

export const downloadInvoiceDump = async (params) => {
  return await GET(
    `${INVOICE_API_BASE_URL}${INVOICE_REPORTS_DOWNLOAD}`,
    params,
    'blob'
  );
};

export const getAgeingViewReports = async (params): Promise<APIResponseType<GetAgeingViewReportData>> => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_AGEING_VIEW_REPORTS}`, params);
};

export const downloadReports = async (params: Partial<typeof ApiDtoInvoicing.FetchAllInvoiceDto> & { category: InvoicingCategory, pageNo: number }) => {
  return await GET(
    `${INVOICE_API_BASE_URL}${INVOICE_REPORTS_DOWNLOAD}`,
    params,
    'blob'
  );
};

export const downloadAgeingViewReports = async (params: Partial<typeof ApiDtoInvoicing.FetchAllInvoiceDto> & { category: InvoicingCategory, pageNo: number }) => {
  return await GET(
    `${INVOICE_API_BASE_URL}${INVOICE_AGEING_VIEW_REPORTS_DOWNLOAD}`,
    params,
    'blob'
  );
};

export const getShipmentBookingMetaReport = async () => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_SHIPMENT_BOOKING_META_REPORT}`, {});
};

export const searchShipmentForInvoice = async (params: typeof ApiDto.SearchShipmentForInvoiceDto): Promise<APIResponseType<CSearchShipmentForInvoiceResponse>> => {
  const res = await POST(`${API_BASE_URL}${INVOICE_SEARCH_SHIPMENT}`, params);
  return {
    ...res,
    data: res.data ? new CSearchShipmentForInvoiceResponse({ data: res.data }) : undefined,
  }
};

export const getChargesList = async (params: {
  toOrganisationId: string;
}): Promise<APIResponseType<GetChargesListData>> => {
  return await GET(`${API_BASE_URL}${GET_CHARGES_LIST}`, params);
};

export const getOrgInvoiceSettings = async (toOrganisationId: string): Promise<APIResponseType<{
  chargeLevelInvoice: boolean;
  defaultCurrency: string;
  useHSNCode: boolean;
  showTeamLevelAccess: boolean;
}>> => {
  return await GET(`${INVOICE_API_BASE_URL}${GET_INVOICE_ORG_SETTINGS(toOrganisationId)}`);
};

export const getHsnCodeList = async (params: {}): Promise<APIResponseType<GetHSNCodeListData>> => {
  return await GET(`${API_BASE_URL}${INQUIRY_HSN_CODE}`, params);
};
export const getImportSetList = async () => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_IMPORT_SET}`);
};

export const downloadVendorPaymentReport = async (params) => {
  return await GET(`${INVOICE_API_BASE_URL}${INVOICE_DOWNLOAD_VENDOR_PAYMENT}`, params, 'blob');
};

export const getInvoiceHistoricalExchangeRate = async (params) => {
  return await POST(`${API_BASE_URL}${INVOICE_HISTORICAL_EXCHANGE_RATE}`, params);
}
